import { homedir } from 'node:os';
import type {
  PluginInstallLocation,
  PluginNotInstalledError,
  PluginScope,
} from '@gitlab-org/duo-plugin-marketplace/node';

const MAX_VISIBLE_INSTALLS = 5;
const SCOPE_SORT: PluginScope[] = ['user', 'project', 'local'];

export function tildify(path: string): string {
  if (process.platform === 'win32') {
    return path;
  }
  const home = homedir();
  if (path === home) {
    return '~';
  }
  return path.startsWith(`${home}/`) ? `~${path.slice(home.length)}` : path;
}

export function describeScope(scope: PluginScope, projectPath?: string): string {
  if (scope === 'user') {
    return 'user';
  }
  return projectPath ? `${scope}: ${tildify(projectPath)}` : scope;
}

export function describePlugin(
  id: string,
  version: string,
  scope: PluginScope,
  projectPath?: string,
): string {
  return `${id} ${version} (${describeScope(scope, projectPath)})`;
}

export function joinWithMore(items: string[]): string {
  const visible = items.slice(0, MAX_VISIBLE_INSTALLS);
  const remaining = items.length - visible.length;
  return remaining > 0 ? `${visible.join(', ')} (+${remaining} more)` : visible.join(', ');
}

export function describeInstalls(
  installs: Pick<PluginInstallLocation, 'scope' | 'projectPath'>[],
): string {
  return joinWithMore(installs.map((install) => describeScope(install.scope, install.projectPath)));
}

/**
 * Orders installs by relevance to the requested location: installs in the
 * requested project first, then by scope (user, project, local), then
 * alphabetically by project path.
 */
function byRelevance(requestedProjectPath?: string) {
  return (a: PluginInstallLocation, b: PluginInstallLocation): number => {
    const aCurrent = a.projectPath === requestedProjectPath ? 0 : 1;
    const bCurrent = b.projectPath === requestedProjectPath ? 0 : 1;
    if (aCurrent !== bCurrent) return aCurrent - bCurrent;
    const scopeDiff = SCOPE_SORT.indexOf(a.scope) - SCOPE_SORT.indexOf(b.scope);
    if (scopeDiff !== 0) return scopeDiff;
    return (a.projectPath ?? '').localeCompare(b.projectPath ?? '');
  };
}

export function formatNotInstalledMessage(error: PluginNotInstalledError): string {
  const location =
    error.scope === 'user' || !error.projectPath ? '' : ` in ${tildify(error.projectPath)}`;
  const firstLine = `Plugin ${error.id} is not installed with ${error.scope} scope${location}.`;
  const lines = [firstLine];
  if (error.installs.length > 0) {
    const sorted = [...error.installs].sort(byRelevance(error.projectPath));
    lines.push(`Installed at: ${describeInstalls(sorted)}`);
  }
  if (!error.id.includes('@')) {
    lines.push('Use the full <plugin>@<marketplace> id.');
  }
  return lines.join('\n');
}
